// ---------------------------------------------------------------------------
// Sprachumschaltung Deutsch / Englisch (14.08.2026, erweitert 14.08.2026 F)
//
// WIE ES ARBEITET
// Jeder uebersetzbare Text steht im Markup mit einem Schluessel:
//
//     <h2 data-i18n="app.011">Hauptmenü</h2>
//     <p  data-i18n-html="app.058">Text mit <b>Auszeichnung</b></p>
//     <input data-i18n-platzhalter="app.115">
//     <button data-i18n-titel="einst.speichernTitel">
//     <button data-i18n-aria="app.111">
//
// `uebersetzeDom()` laeuft einmal ueber das Dokument und setzt die Texte
// der eingestellten Sprache ein. Im HTML steht weiterhin die deutsche
// Fassung - ohne JavaScript sieht die Seite also aus wie bisher, statt
// leere Kaesten zu zeigen. Das Woerterbuch liegt in i18n-texte.js.
//
// WARUM KEINE FERTIGE BIBLIOTHEK
// Die App ist Vanilla JS ohne Framework. i18next & Co. braechten einen
// Baukasten fuer Pluralformen, Namensraeume und Ladeketten mit, von dem
// hier nichts gebraucht wird - ein Woerterbuch und eine Schleife ueber
// data-Attribute sind der ganze Bedarf.
//
// ── DIE BESITZ-PRUEFUNG (der wichtigste Teil dieser Datei) ────────────────
//
// Ein Sprachwechsel mitten im Spiel darf keinen Punktestand zuruecksetzen.
// Genau das wuerde aber passieren, wenn `uebersetzeDom()` stur alles neu
// beschriebe: `<div id="p1-score" data-i18n="...">501</div>` steht laengst
// auf 140, und der Schluessel kennt nur die 501.
//
// Deshalb merkt sich diese Datei in einer WeakMap, WAS sie zuletzt in ein
// Element geschrieben hat. Beim naechsten Durchlauf gilt:
//
//   * noch nie beschrieben          -> schreiben
//   * Inhalt = unser letzter Wert   -> schreiben (niemand war dazwischen)
//   * Inhalt hat sich geaendert     -> NICHT anfassen; das Element gehoert
//                                      jetzt dem Spiel, nicht der Sprache
//
// Damit ist die Umschaltung unabhaengig davon, wie sorgfaeltig die
// Schluessel gesetzt wurden - ein versehentlich markierter Punktestand
// kann keinen Schaden mehr anrichten. Die WeakMap haelt keine Elemente am
// Leben; entfernte Knoten werden ganz normal eingesammelt.
//
// STAND DER ABDECKUNG
// Zweisprachig sind: Startseite und alle Spielbildschirme, Einstellungen,
// Statistik, Kontakt, Admin, Impressum und Datenschutz, dazu die Texte,
// die JavaScript erzeugt. Was neu ins Markup kommt, braucht ein
// data-i18n-Attribut und einen Eintrag in i18n-texte.js - sonst bleibt es
// deutsch, faellt aber nicht auf die Nase.
// ---------------------------------------------------------------------------

import TEXTE from "./i18n-texte.js";

const SPRACHE_KEY = "secure-darts:sprache";
const SPRACHEN = ["de", "en"];

function gespeicherteSprache() {
  try {
    const wert = localStorage.getItem(SPRACHE_KEY);
    if (SPRACHEN.includes(wert)) return wert;
  } catch (error) {
    /* Speicher gesperrt - dann die Browsersprache */
  }
  const browser = String(
    (typeof navigator !== "undefined" && navigator.language) || "de",
  ).toLowerCase();
  return browser.startsWith("en") ? "en" : "de";
}

let aktuelleSprache = gespeicherteSprache();

function sprache() {
  return aktuelleSprache;
}

/** Ein Text in der aktuellen Sprache. Unbekannter Schluessel gibt die
 *  Vorgabe zurueck (oder den Schluessel selbst) - eine Luecke im
 *  Woerterbuch soll auffallen, aber nichts zerstoeren. */
function t(schluessel, vorgabe = "") {
  const tabelle = TEXTE[aktuelleSprache] || TEXTE.de;
  return tabelle[schluessel] ?? TEXTE.de[schluessel] ?? vorgabe ?? schluessel;
}

/** Wie t(), aber mit Platzhaltern:
 *      tf("js.spielerWirft", { name: "Pat" })  ->  "Pat is throwing..."
 *  Getrennte Funktion, damit der haeufige Fall ohne Platzhalter keine
 *  Ersetzung kostet - und damit im Code sichtbar ist, welche Texte
 *  Variablen enthalten. */
function tf(schluessel, werte = {}, vorgabe = "") {
  return String(t(schluessel, vorgabe)).replace(
    /\{(\w+)\}/g,
    (ganz, name) => (name in werte ? String(werte[name]) : ganz),
  );
}

// Was wir zuletzt in ein Element geschrieben haben (siehe Modulkopf).
const besitz = new WeakMap();

function darfSchreiben(el, feld, jetzt) {
  const notiz = besitz.get(el);
  if (!notiz || !(feld in notiz)) return true; // noch nie beschrieben
  return notiz[feld] === jetzt; // nur, wenn seither niemand dazwischenkam
}

function merke(el, feld, wert) {
  let notiz = besitz.get(el);
  if (!notiz) {
    notiz = {};
    besitz.set(el, notiz);
  }
  notiz[feld] = wert;
}

function setzeEigenschaft(el, feld, text) {
  if (!text) return;
  if (!darfSchreiben(el, feld, el[feld])) return;
  el[feld] = text;
  // Nicht `text` merken, sondern das, was danach WIRKLICH drinsteht: Der
  // Browser normalisiert innerHTML (Anfuehrungszeichen, Attributreihen-
  // folge). Merkten wir die Vorlage, hielte sich das Element beim
  // naechsten Durchlauf faelschlich fuer fremdbeschrieben.
  merke(el, feld, el[feld]);
}

function setzeAttribut(el, attr, text) {
  if (!text) return;
  if (!darfSchreiben(el, attr, el.getAttribute(attr))) return;
  el.setAttribute(attr, text);
  merke(el, attr, text);
}

/** Alle Elemente mit data-i18n* unter `wurzel` uebersetzen. */
function uebersetzeDom(wurzel = document) {
  if (!wurzel || typeof wurzel.querySelectorAll !== "function") return;

  for (const el of wurzel.querySelectorAll("[data-i18n]")) {
    setzeEigenschaft(el, "textContent", t(el.dataset.i18n, el.textContent));
  }
  // Getrennt von data-i18n, weil hier innerHTML gesetzt wird. Der Inhalt
  // kommt ausschliesslich aus dem eigenen Woerterbuch, nie von aussen -
  // sonst waere das eine Einladung, Markup einzuschleusen.
  for (const el of wurzel.querySelectorAll("[data-i18n-html]")) {
    setzeEigenschaft(el, "innerHTML", t(el.dataset.i18nHtml, el.innerHTML));
  }
  for (const el of wurzel.querySelectorAll("[data-i18n-platzhalter]")) {
    setzeAttribut(
      el,
      "placeholder",
      t(el.dataset.i18nPlatzhalter, el.placeholder),
    );
  }
  for (const el of wurzel.querySelectorAll("[data-i18n-titel]")) {
    setzeAttribut(el, "title", t(el.dataset.i18nTitel, el.title));
  }
  for (const el of wurzel.querySelectorAll("[data-i18n-aria]")) {
    setzeAttribut(
      el,
      "aria-label",
      t(el.dataset.i18nAria, el.getAttribute("aria-label")),
    );
  }

  if (document.documentElement) {
    document.documentElement.lang = aktuelleSprache;
  }
}

/** Sprache setzen, speichern, Oberflaeche sofort umschreiben. */
function setzeSprache(code) {
  if (!SPRACHEN.includes(code)) return aktuelleSprache;
  aktuelleSprache = code;
  try {
    localStorage.setItem(SPRACHE_KEY, code);
  } catch (error) {
    /* dann gilt sie bis zum Neuladen */
  }
  uebersetzeDom();
  try {
    window.dispatchEvent(
      new CustomEvent("secure-darts:sprache", { detail: { sprache: code } }),
    );
  } catch (error) {
    /* CustomEvent fehlt nur in sehr alten Umgebungen */
  }
  return code;
}

if (typeof window !== "undefined") {
  const start = () => uebersetzeDom();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}

Object.assign(window, { t, tf, sprache, setzeSprache, uebersetzeDom });

export { t, tf, sprache, setzeSprache, uebersetzeDom, SPRACHEN };
